import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';

import { BudgetCalculationService } from '../../budget/budget-calculation.service';
import { BudgetParityService } from '../../budget/budget-parity.service';
import { FetchEmployeesWithBudgets, GetBudgetedCostsForYear } from '../../budget/budget.actions';
import { BudgetState } from '../../budget/budget.state';
import { TurnoverData } from '../../models/Financial/TurnoverData';
import { TurnoverDataPerMonth } from '../../models/Financial/TurnoverDataPerMonth';
import { InvoiceType } from '../../models/GlobalFile';
import { InformationForFinancialData } from '../../models/InformationForFinancialData';
import { DeleteLinkedObject } from '../../shared/global-files/global-files.actions';
import { TimesheetsService } from '../../timesheets/timesheets.service';
import { DATASETS } from '../employee-graph/employee-graph.component';
import { GetAllFinancialInformationForCategory, GetAllFinancialInformationForEmployee, GetAllFinancialInformationForProject } from '../financial.actions';
import { FinancialService } from '../financial.service';
import { FinancialState } from '../financial.state';

// import { SettingsState } from '../../settings/settings.state';

@Component({
    selector: 'app-chart-information-modal',
    templateUrl: './chart-information-modal.component.html',
    styleUrls: ['./chart-information-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ChartInformationModalComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() month: number;
    @Input() year: number;
    @Input() dataset: DATASETS;
    @Input() chartInformationObjectType: ChartInformationObjectType;
    @Input() objectId: string;
    @Input() title: string;
    @Input() turnoverDataInformation: TurnoverDataPerMonth;

    saleInformationDatasource: MatTableDataSource<InformationForFinancialData> = new MatTableDataSource<InformationForFinancialData>();
    saleDisplayedColumns = ['title', 'invoiceNumber', 'amount'];

    purchaseInformationDatasource: MatTableDataSource<InformationForFinancialData> = new MatTableDataSource<InformationForFinancialData>();
    purchaseDisplayedColumns = ['title', 'hours', 'hourlyCost', 'amount'];

    turnoverInformationDatasource: MatTableDataSource<TurnoverData> = new MatTableDataSource<TurnoverData>();
    turnoverDisplayedColumns = ['title', 'amount'];

    @Select(FinancialState.informationForFinancialData) informationForFinancialData$: Observable<InformationForFinancialData[]>;
    date: Date;

    // Expose enum to template
    ChartInformationObjectType = ChartInformationObjectType;

    @Input() purchaseInformation: InformationForFinancialData[] = [];
    @Input() saleInformation: InformationForFinancialData[] = [];

    @ViewChild('turnoverSort') turnoverSort: MatSort = new MatSort();
    private sSort: MatSort;
    private pSort: MatSort;
    private subscriptions = new Subscription();
    unlinkingInvoiceId: string | null = null;
    constructor(
        public activeModal: NgbActiveModal,
        private store: Store,
        private financialService: FinancialService,
        private timesheetsService: TimesheetsService,
        private budgetCalc: BudgetCalculationService,
        private budgetParity: BudgetParityService,
        private toastr: ToastrService,
    ) {}

    // Cost display mode for EMPLOYEE modal: 'hours' uses hourly rate × hours, 'budgets' allocates the fixed monthly budget over worked project hours.
    costMode: 'budgets' | 'hours' = 'hours';

    @ViewChild('saleSort') set saleSort(ms: MatSort) {
        this.sSort = ms;
        this.setDataSourceAttributesForSaleSort();
    }

    @ViewChild('purchaseSort') set purchaseSort(ms: MatSort) {
        this.pSort = ms;
        this.setDataSourceAttributesForPurchaseSort();
    }

    setDataSourceAttributesForSaleSort() {
        this.saleInformationDatasource.sort = this.sSort;
    }

    setDataSourceAttributesForPurchaseSort() {
        this.purchaseInformationDatasource.sort = this.pSort;
    }

    ngAfterViewInit() {
        this.turnoverInformationDatasource.sort = this.turnoverSort;
    }

    ngOnInit(): void {
        this.createMatTables();

        this.date = new Date(this.year, this.month);
        //When the chartInformationObjectType is TOTAL the information is passed along from caller
        if (this.chartInformationObjectType === ChartInformationObjectType.TOTAL) {
            // Use provided purchase/sale information directly
            this.purchaseInformationDatasource.data = this.purchaseInformation || [];
            this.saleInformationDatasource.data = this.saleInformation || [];

            this.purchaseInformationDatasource.sort = this.pSort;
            this.saleInformationDatasource.sort = this.sSort;
            // For TOTAL (Overhead), show only Title and Amount columns for Cost
            this.purchaseDisplayedColumns = ['title', 'amount'];
            return;
        }

        if (this.objectId) {
            if (this.chartInformationObjectType === ChartInformationObjectType.EMPLOYEE)
                this.store.dispatch(new GetAllFinancialInformationForEmployee(this.objectId, this.month + 1, this.year));
            else if (this.chartInformationObjectType === ChartInformationObjectType.PROJECT)
                this.store.dispatch(new GetAllFinancialInformationForProject(this.objectId, this.month + 1, this.year));
            else if (this.chartInformationObjectType === ChartInformationObjectType.CATEGORY)
                this.store.dispatch(new GetAllFinancialInformationForCategory(this.objectId, this.month + 1, this.year));

            if (this.chartInformationObjectType === ChartInformationObjectType.CATEGORY) {
                this.purchaseDisplayedColumns = ['title', 'amount', 'actions'];
            }
            this.saleDisplayedColumns = ['title', 'invoiceNumber', 'amount', 'actions'];

            this.subscriptions.add(
                this.informationForFinancialData$.subscribe((information) => {
                    // Ignore reset-to-null emissions so the current modal content does not flicker/clear.
                    if (information == null) {
                        return;
                    }

                    const items = information;
                    const nextPurchaseInformation = items.filter((info) => info.invoiceType === InvoiceType[InvoiceType.PURCHASE]);
                    const nextSaleInformation = items.filter((info) => info.invoiceType === InvoiceType[InvoiceType.SALE]);

                    this.purchaseInformation = nextPurchaseInformation;
                    this.saleInformation = nextSaleInformation;

                    this.purchaseInformationDatasource.data = this.purchaseInformation;
                    this.saleInformationDatasource.data = this.saleInformation;

                    this.purchaseInformationDatasource.sort = this.pSort;
                    this.saleInformationDatasource.sort = this.sSort;

                    // If PROJECT, fetch timesheets for this month/year to compute hours for each employee in cost table
                    if (this.chartInformationObjectType === ChartInformationObjectType.PROJECT) {
                        this.populateHoursForCosts();
                    }

                    // If EMPLOYEE, compute per-project hours for the employee and apply hourly cost rate
                    if (this.chartInformationObjectType === ChartInformationObjectType.EMPLOYEE) {
                        this.populateEmployeeProjectCosts();
                    }
                }),
            );

            // Ensure budgeted costs are loaded for parity; if missing, dispatch and recompute
            const budgetedCosts = this.store.selectSnapshot(BudgetState.budgetedCostsByYear(this.year)) as any[];
            if (!budgetedCosts || !budgetedCosts.length) {
                this.store.dispatch(new GetBudgetedCostsForYear(this.year)).subscribe(() => {
                    if (this.chartInformationObjectType === ChartInformationObjectType.EMPLOYEE) {
                        // Recompute with freshly loaded state
                        this.populateEmployeeProjectCosts();
                    }
                });
            }
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    /** Toggle cost mode and recompute employee project costs when changed */
    setCostMode(mode: 'budgets' | 'hours') {
        if (this.costMode === mode) return;
        this.costMode = mode;
        if (this.chartInformationObjectType === ChartInformationObjectType.EMPLOYEE) {
            // Recompute the project rows with the new cost mode
            this.populateEmployeeProjectCosts();
        }
    }

    unlinkInvoice(info: InformationForFinancialData, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        if (!info?.objectId || !this.objectId) {
            return;
        }
        this.unlinkingInvoiceId = info.objectId;
        this.store.dispatch(new DeleteLinkedObject(info.objectId, this.objectId)).subscribe({
            next: () => {
                this.unlinkingInvoiceId = null;
                this.saleInformation = (this.saleInformation || []).filter((i) => i.objectId !== info.objectId);
                this.purchaseInformation = (this.purchaseInformation || []).filter((i) => i.objectId !== info.objectId);
                this.saleInformationDatasource.data = this.saleInformation;
                this.purchaseInformationDatasource.data = this.purchaseInformation;
                this.toastr.success('Link verwijderd');
            },
            error: () => {
                this.unlinkingInvoiceId = null;
                this.toastr.error('Fout bij verwijderen van link');
            },
        });
    }

    calculateTotal(information: InformationForFinancialData[]): number {
        // Generic total for given information (used for Income table)
        return (information || []).reduce((total: number, info: any) => total + (Number(info?.amount) || 0), 0);
    }

    // Total for Purchase (cost) using enriched datasource (used for Cost table)
    calculateTotalPurchase(): number {
        return (this.purchaseInformationDatasource?.data || []).reduce((total: number, row: any) => total + (Number(row?.amount) || 0), 0);
    }

    calculateTotalPurchaseHours(): number {
        return (this.purchaseInformationDatasource?.data || []).reduce((total: number, row: any) => total + (Number(row?._hours) || 0), 0);
    }

    calculateTotalForTurnover(): number {
        return this.turnoverDataInformation.turnoverData.reduce((total: number, turnoverData: TurnoverData) => total + turnoverData.amount, 0);
    }

    calculateMargin(): number {
        const income: number = this.calculateTotal(this.saleInformation);
        // For PROJECT and EMPLOYEE, use datasource data to get correct budgeted costs
        const useDatasource =
            this.chartInformationObjectType === ChartInformationObjectType.PROJECT || this.chartInformationObjectType === ChartInformationObjectType.EMPLOYEE;
        const cost: number = useDatasource
            ? (this.purchaseInformationDatasource?.data || []).reduce((total: number, row: any) => total + (Number(row?.amount) || 0), 0)
            : this.calculateTotal(this.purchaseInformation);

        return income - cost;
    }

    private createMatTables() {
        if (this.turnoverDataInformation) {
            this.turnoverInformationDatasource.data = this.turnoverDataInformation.turnoverData;
        }
    }

    // Compute hours and correct budgeted costs per employee for the selected project month (using BudgetCalculationService)
    private populateHoursForCosts(): void {
        const year = this.year;
        const month = this.month; // 0-11
        this.financialService.getTimesheetsForProject(this.objectId, year, month).subscribe((timesheets: any[]) => {
            // If no timesheets for this month, don't enrich with budget costs
            if (!timesheets || timesheets.length === 0) {
                console.log('[populateHoursForCosts] No timesheets for this month, skipping budget enrichment');
                return;
            }

            const employeeMap: Record<string, { name: string; hours: number; uuid: string }> = {};

            timesheets.forEach((ts: any) => {
                const emp = ts?.employee;
                if (!emp) return;
                const empId = emp.uuid;
                const name = `${emp.firstName} ${emp.lastName}`;
                const timeslots = ts?.timeslots || [];
                let minutesForProject = 0;
                timeslots.forEach((slot: any) => {
                    if (slot?.project?.uuid === this.objectId) {
                        minutesForProject += Number(slot?.minutes || 0);
                    }
                });
                const hours = minutesForProject / 60;
                if (!employeeMap[empId]) {
                    employeeMap[empId] = { name, hours: 0, uuid: empId };
                }
                employeeMap[empId].hours += hours;
            });

            if (!Object.keys(employeeMap).length) return;

            const employeesWithBudgets = this.store.selectSnapshot(BudgetState.EmployeesWithBudgets);
            if (!employeesWithBudgets) {
                this.store.dispatch(new FetchEmployeesWithBudgets()).subscribe(() => this.applyBudgetCosts(employeeMap));
            } else {
                this.applyBudgetCosts(employeeMap);
            }
        });
    }

    /** Apply budget costs using BudgetState data */
    private applyBudgetCosts(employeeMap: Record<string, { name: string; hours: number; uuid: string }>): void {
        const employeesWithBudgets = this.store.selectSnapshot(BudgetState.EmployeesWithBudgets) || [];
        const targetDate = new Date(this.year, this.month, 15); // middle of month to ensure inclusion
        const hoursMap: Record<string, number> = {};
        const hourlyCostMap: Record<string, number> = {};
        const projectCostMap: Record<string, number> = {};

        Object.keys(employeeMap).forEach((empId) => {
            const empData = employeeMap[empId];
            const match = employeesWithBudgets.find((e: any) => e.uuid === empId);
            const budgets = match?.budgets || [];
            const workPercentage = match?.workPercentage; // e.g., 100, 80, 60
            const monthlyTotal = this.budgetCalc.computeMonthlyBudgetTotal(budgets, targetDate);

            // Use budgeted hourly cost (from BudgetState monthly totals parity) as the source of truth
            const budgetedCosts = this.store.selectSnapshot(BudgetState.budgetedCostsByYear(targetDate.getFullYear())) as any[];
            let budgetedHourlyCost = this.budgetParity.getHourlyCostFromBudgetedState(
                empId,
                targetDate.getFullYear(),
                targetDate.getMonth(),
                budgetedCosts,
                employeesWithBudgets,
            );
            if (budgetedHourlyCost == null) {
                // Try computing from monthly total in BudgetState directly (robust key matching)
                const entry = (budgetedCosts || []).find((e: any) => e.employeeId === empId || e.uuid === empId || e.id === empId);
                const monthlyTotalFromState = entry?.costs?.[targetDate.getMonth()];
                if (monthlyTotalFromState != null && typeof workPercentage === 'number') {
                    const adjustedHours = 17 * 8 * (workPercentage / 100);
                    budgetedHourlyCost = Number((monthlyTotalFromState / adjustedHours).toFixed(2));
                } else {
                    // Fallback to pure budgets calc
                    budgetedHourlyCost = this.budgetParity.getHourlyBudgetedCostForEmployee(empId, targetDate, employeesWithBudgets);
                }
            }

            // Project cost = budgeted hourly × actual hours for this project
            const projectCost = (Number(budgetedHourlyCost) || 0) * (empData.hours || 0);

            hoursMap[empData.name] = empData.hours;
            hourlyCostMap[empData.name] = budgetedHourlyCost;
            projectCostMap[empData.name] = projectCost;
            // Scoped debug: only log for Jef
            if ((empData.name || '').toLowerCase().includes('jef')) {
                console.log('[BudgetCalc]', empData.name, { monthlyTotal, hours: empData.hours, workPercentage, budgetedHourlyCost, projectCost });
            }
        });

        // Replace existing rows with a unique per-employee row built from aggregated hours
        const employeeRows = Object.keys(hoursMap).map(
            (name) =>
                ({
                    objectId: '',
                    title: name,
                    invoiceType: InvoiceType[InvoiceType.PURCHASE],
                    invoiceNumber: '',
                    amount: projectCostMap[name] || 0,
                    _hours: hoursMap[name] || 0,
                    _hourlyCost: hourlyCostMap[name] || 0,
                } as any),
        );

        this.purchaseInformationDatasource.data = employeeRows as any;
    }

    /** Compute per-project costs for an EMPLOYEE: hours per project × employee hourly rate */
    private populateEmployeeProjectCosts(): void {
        const year = this.year;
        const month = this.month; // 0-11

        // Fetch timesheets for the employee directly and compute per-project hours for the selected month
        this.timesheetsService.fetchTimesheetsForEmployee(this.objectId).subscribe((timesheets) => {
            if (!timesheets || !timesheets.length) return;

            // Filter timesheets for selected month/year
            const monthTimesheets = timesheets.filter((ts: any) => {
                if (!ts?.expirationDate) return false;
                const d = new Date(ts.expirationDate.toString());
                return d.getFullYear() === year && d.getMonth() === month;
            });
            if (!monthTimesheets.length) return;

            // Aggregate hours per project
            const projectHours: Record<string, { title: string; hours: number }> = {};
            monthTimesheets.forEach((ts: any) => {
                (ts?.timeslots || []).forEach((slot: any) => {
                    const projectUuid = slot?.project?.uuid || slot?.project?.title || 'Unknown';
                    const title = slot?.project?.title || slot?.project?.companyName || 'Unknown project';
                    const minutes = Number(slot?.minutes || 0);
                    const hours = minutes / 60;
                    if (!projectHours[projectUuid]) projectHours[projectUuid] = { title, hours: 0 };
                    projectHours[projectUuid].hours += hours;
                });
            });

            // Get hourly rate from budgets overview for this employee and month
            const employeesWithBudgets = this.store.selectSnapshot(BudgetState.EmployeesWithBudgets) || [];
            const targetDate = new Date(year, month, 15);
            // Prefer BudgetState.budgetedCostsByYear for exact monthly totals parity, fallback to pure budgets calc
            const budgetedCosts = this.store.selectSnapshot(BudgetState.budgetedCostsByYear(year)) as any[];
            const budgetedCostEntry = this.findBudgetedCostEntry(budgetedCosts, this.objectId, year);
            const monthlyTotalFromState = budgetedCostEntry?.costs?.[month] ?? null;
            let hourlyCost = this.budgetParity.getHourlyCostFromBudgetedState(this.objectId, year, month, budgetedCosts, employeesWithBudgets);
            if (hourlyCost == null) {
                // Try compute from BudgetState monthly total with robust key matching
                const matchEmp = employeesWithBudgets.find((e: any) => e.uuid === this.objectId);
                const workPct = matchEmp?.workPercentage;
                if (monthlyTotalFromState != null && typeof workPct === 'number') {
                    const adjustedHours = 17 * 8 * (workPct / 100);
                    hourlyCost = Number((monthlyTotalFromState / adjustedHours).toFixed(2));
                } else {
                    // Fallback to pure budgets calc
                    hourlyCost = this.budgetParity.getHourlyBudgetedCostForEmployee(this.objectId, targetDate, employeesWithBudgets);
                }
            }

            // Total hours across projects for proportional distribution if needed
            const totalHours = Object.values(projectHours).reduce((s, ph) => s + ph.hours, 0);

            // Build purchase rows depending on cost mode:
            // - 'hours': amount = hourlyCost * hours
            // - 'budgets': if BudgetState monthly total available, distribute monthly total proportionally to project hours
            const enrichedRows: InformationForFinancialData[] = Object.values(projectHours).map((ph) => {
                let amount = 0;
                let displayedHourlyCost = hourlyCost || 0;
                if (this.costMode === 'budgets' && monthlyTotalFromState != null && totalHours > 0) {
                    amount = (monthlyTotalFromState || 0) * (ph.hours / totalHours);
                    displayedHourlyCost = ph.hours > 0 ? amount / ph.hours : 0;
                } else {
                    amount = (hourlyCost || 0) * ph.hours;
                }
                return {
                    objectId: '',
                    title: ph.title,
                    amount,
                    invoiceType: InvoiceType[InvoiceType.PURCHASE],
                    invoiceNumber: '',
                    // augment with display-only fields used by template
                    _hours: ph.hours,
                    _hourlyCost: displayedHourlyCost,
                } as any;
            });

            // Replace purchase datasource with computed project cost rows
            // Replace incoming budget rows with per-project computed cost rows
            this.purchaseInformationDatasource.data = enrichedRows as any;
            this.purchaseInformationDatasource.sort = this.pSort;
        });
    }

    private findBudgetedCostEntry(budgetedCosts: any[], employeeId: string, year: number): any {
        return (budgetedCosts || []).find(
            (entry: any) =>
                (entry.employeeId === employeeId || entry.uuid === employeeId || entry.id === employeeId) && (entry.year === undefined || entry.year === year),
        );
    }
}

export enum ChartInformationObjectType {
    EMPLOYEE,
    PROJECT,
    CATEGORY,
    TURNOVER,
    TOTAL,
}
